import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import type { Review } from "@/modules/review/types";
import VideoModal from "@/modules/review/components/video-modal";
import { useWidgetFont } from "../hooks/use-widget-font";
import type { WidgetTemplateProps } from "./template-router";

// Helper functions
function getInitials(name: string) {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase();
}

function getGradient(name: string) {
	const gradients = [
		"from-violet-500 to-purple-600",
		"from-blue-500 to-cyan-500",
		"from-emerald-500 to-teal-500",
		"from-orange-500 to-amber-500",
		"from-pink-500 to-rose-500",
		"from-indigo-500 to-blue-500",
	];
	const index = name.charCodeAt(0) % gradients.length;
	return gradients[index];
}

function AudioPlayer({ src, isDark, initialDuration }: { src: string; isDark: boolean; initialDuration?: number | null }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);

	const formatTime = (time: number) => {
		if (isNaN(time) || !isFinite(time)) return "0:00";
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	const togglePlay = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleTimeUpdate = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			setCurrentTime(audioRef.current.currentTime);
			if (!duration) {
				setDuration(audioDur);
			}
		}
	};

	const handleLoadedMetadata = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0 && !initialDuration) {
			setDuration(audioDur);
		}
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const time = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = time;
			setCurrentTime(time);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
		setCurrentTime(0);
	};

	const progress = duration > 0 && isFinite(duration) ? Math.min((currentTime / duration) * 100, 100) : 0;

	return (
		<div className={`mt-6 mb-4 p-3 rounded-xl ${isDark ? "bg-gray-500/30" : "bg-gray-100"}`}>
			<audio ref={audioRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />
			<div className="flex items-center gap-3">
				<button
					type="button"
					onClick={togglePlay}
					className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 shadow-sm ${isDark ? "text-white bg-gray-700" : "text-black bg-white"}`}
				>
					{isPlaying ? (
						<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
						</svg>
					) : (
						<svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
					)}
				</button>
				<div className="flex-1 flex items-center gap-2">
					<span className={`text-xs tabular-nums ${isDark ? "text-gray-400" : "text-gray-500"}`}>{formatTime(currentTime)}</span>
					<div className="flex-1 relative h-1.5 rounded-full overflow-hidden bg-gray-300/50">
						<div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: "#1f1f1f" }} />
						<input type="range" min={0} max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
					</div>
					{duration >= 1 && <span className={`text-xs tabular-nums ${isDark ? "text-gray-400" : "text-gray-500"}`}>{formatTime(duration)}</span>}
				</div>
			</div>
		</div>
	);
}

function LoaderRing({ isDark }: { isDark: boolean }) {
	return (
		<div className="flex items-center justify-center min-h-[50vh]">
			<div className={`w-8 h-8 rounded-full border-2 border-t-transparent animate-spin ${isDark ? "border-gray-500" : "border-gray-400"}`} />
		</div>
	);
}

type Theme = "light" | "dark" | "system-default";

const sampleReviews: Review[] = [
	{
		id: "1",
		workspace_id: "",
		form_id: null,
		date: "",
		type: "text",
		rating: 5,
		content_type: "text",
		content_text: "This platform has completely transformed how we collect and showcase customer feedback. The wall of love feature is a game-changer for our marketing efforts!",
		private_feedback: null,
		consent: null,
		video_url: null,
		video_thumbnail_url: null,
		video_duration: null,
		video_file_size: null,
		video_status: null,
		mux_upload_id: null,
		mux_asset_id: null,
		mux_playback_id: null,
		audio_url: null,
		audio_duration: null,
		customer_name: "Sarah Chen",
		customer_headline: "Product Manager at TechFlow",
		customer_avatar_url: null,
		customer_email: "",
		customer_phone: null,
		customer_company_name: "TechFlow",
		customer_job_title: "Product Manager",
		customer_company_website_url: null,
		customer_company_logo_url: null,
		source: "manual",
		source_post_url: null,
		status: "published",
		published_at: null,
		tags: [],
		created_by_id: "",
		created_at: "",
		updated_by_id: "",
		updated_at: "",
		deleted_by_id: null,
		deleted_at: null,
	},
	{
		id: "2",
		workspace_id: "",
		form_id: null,
		date: "",
		type: "text",
		rating: 5,
		content_type: "text",
		content_text: "Absolutely love it! Our conversion rates increased by 40% after adding the testimonial widget to our landing page.",
		private_feedback: null,
		consent: null,
		video_url: null,
		video_thumbnail_url: null,
		video_duration: null,
		video_file_size: null,
		video_status: null,
		mux_upload_id: null,
		mux_asset_id: null,
		mux_playback_id: null,
		audio_url: null,
		audio_duration: null,
		customer_name: "Michael Roberts",
		customer_headline: "CEO at StartupX",
		customer_avatar_url: null,
		customer_email: "",
		customer_phone: null,
		customer_company_name: "StartupX",
		customer_job_title: "CEO",
		customer_company_website_url: null,
		customer_company_logo_url: null,
		source: "manual",
		source_post_url: null,
		status: "published",
		published_at: null,
		tags: [],
		created_by_id: "",
		created_at: "",
		updated_by_id: "",
		updated_at: "",
		deleted_by_id: null,
		deleted_at: null,
	},
	{
		id: "3",
		workspace_id: "",
		form_id: null,
		date: "",
		type: "text",
		rating: 5,
		content_type: "text",
		content_text: "The best testimonial management tool I've ever used. Clean interface, powerful features, and excellent support team.",
		private_feedback: null,
		consent: null,
		video_url: null,
		video_thumbnail_url: null,
		video_duration: null,
		video_file_size: null,
		video_status: null,
		mux_upload_id: null,
		mux_asset_id: null,
		mux_playback_id: null,
		audio_url: null,
		audio_duration: null,
		customer_name: "Emily Watson",
		customer_headline: "Marketing Director at GrowthLabs",
		customer_avatar_url: null,
		customer_email: "",
		customer_phone: null,
		customer_company_name: "GrowthLabs",
		customer_job_title: "Marketing Director",
		customer_company_website_url: null,
		customer_company_logo_url: null,
		source: "manual",
		source_post_url: null,
		status: "published",
		published_at: null,
		tags: [],
		created_by_id: "",
		created_at: "",
		updated_by_id: "",
		updated_at: "",
		deleted_by_id: null,
		deleted_at: null,
	},
];

export default function TemplateClassicMasonryGrid({ previewData, layoutConfig, templateDetail, showBranding = true, nextCursor, onLoadMore, isLoadingMore = false }: WidgetTemplateProps) {
	const fontStyle = useWidgetFont(layoutConfig, templateDetail);

	const getLayoutValue = useCallback(
		(key: string, fallback: unknown = "") => {
			if (layoutConfig[key] !== undefined) return layoutConfig[key];
			if (templateDetail?.defaults?.layout?.[key] !== undefined) return templateDetail.defaults.layout[key];
			return fallback;
		},
		[layoutConfig, templateDetail],
	);

	// Video modal state
	const [activeVideoPlaybackId, setActiveVideoPlaybackId] = useState<string | null>(null);

	// Theme handling
	const theme = (getLayoutValue("theme", "light") as Theme);
	const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
		if (typeof window !== "undefined") {
			return window.matchMedia("(prefers-color-scheme: dark)").matches;
		}
		return false;
	});

	useEffect(() => {
		if (theme !== "system-default") return;
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
		mediaQuery.addEventListener("change", handler);
		return () => mediaQuery.removeEventListener("change", handler);
	}, [theme]);

	const isDark = useMemo(() => {
		if (theme === "dark") return true;
		if (theme === "light") return false;
		return systemPrefersDark;
	}, [theme, systemPrefersDark]);

	// Layout config values
	const columns = Number(getLayoutValue("columns", 3));
	const cardSpacing = `${Number(getLayoutValue("card_spacing", 32))}px`;
	const showRating = getLayoutValue("show_rating", true) !== false;
	const showAvatar = getLayoutValue("show_avatar", true) !== false;
	const ratingStarColor = String(getLayoutValue("rating_star_color", "") || "") || undefined;

	// Reviews
	const reviews = useMemo(() => {
		if (!previewData) return sampleReviews;
		if (!("reviews" in previewData) || !previewData.reviews || previewData.reviews.data.length === 0) return sampleReviews;
		return previewData.reviews.data;
	}, [previewData]);

	const isLoading = !previewData;

	return (
		<div className={`min-h-50 ${isDark ? "bg-gray-900" : "bg-gray-50"}`} style={fontStyle}>
			<div className="relative z-10">
				{isLoading ? (
					<LoaderRing isDark={isDark} />
				) : reviews.length === 0 ? (
					<div className={`py-12 sm:py-16 md:py-20 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
						<div className="max-w-md mx-auto px-4">
							<svg className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 ${isDark ? "text-gray-700" : "text-gray-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
								/>
							</svg>
							<h3 className={`text-lg sm:text-xl font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>No reviews yet</h3>
							<p className={`text-sm sm:text-base leading-relaxed ${isDark ? "text-gray-500" : "text-gray-600"}`}>
								Check back soon to see what our customers are saying about us!
							</p>
						</div>
					</div>
				) : (
					<ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 768: Math.min(columns, 2), 1024: columns }} gutterBreakPoints={{ 0: cardSpacing }}>
						<Masonry>
							{reviews.map((review) => {
								const customerInfo =
									review.customer_job_title && review.customer_company_name
										? `${review.customer_job_title} at ${review.customer_company_name}`
										: review.customer_headline || review.customer_company_name || "";

								return (
									<div className="w-full" key={review.id}>
										<div
											className={`group relative rounded-2xl p-5 transition-all duration-300 border ${
												isDark ? "bg-gray-800 hover:bg-gray-750 border-gray-700 hover:border-gray-600" : "bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
												{review.content_type === "video" ? (
													<svg className={`w-8 h-8 ${isDark ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 14 14">
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="1"
															d="M1.14 9.084c0.167 1.435 1.36 2.566 2.8 2.697 0.996 0.09 2.018 0.167 3.06 0.167 1.042 0 2.064 -0.077 3.06 -0.167 1.44 -0.13 2.633 -1.262 2.8 -2.697 0.08 -0.682 0.14 -1.378 0.14 -2.084 0 -0.706 -0.06 -1.402 -0.14 -2.084 -0.167 -1.435 -1.36 -2.566 -2.8 -2.697 -0.996 -0.09 -2.018 -0.167 -3.06 -0.167 -1.042 0 -2.064 0.077 -3.06 0.167 -1.44 0.131 -2.633 1.262 -2.8 2.697A17.91 17.91 0 0 0 1 7c0 0.706 0.06 1.402 0.14 2.084Z"
														/>
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="1"
															d="M6.784 9.26C8.008 8.604 9.595 7.632 9.595 7c0 -0.632 -1.587 -1.604 -2.811 -2.26 -0.643 -0.344 -1.393 0.132 -1.393 0.861l0 2.798c0 0.729 0.75 1.205 1.393 0.86Z"
														/>
													</svg>
												) : review.content_type === "audio" ? (
													<svg className={`w-8 h-8 ${isDark ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 14 14">
														<path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6.93848 11.3687v1.8819" strokeWidth="1" />
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M6.98161 0.765137c1.54089 0 2.93578 0.917993 2.96889 2.235543 0.0562 2.23593 0.5798 5.78139 -2.93227 5.78139 -3.51209 0 -3.02508 -3.54546 -2.96889 -5.78139C4.08245 1.68313 5.44072 0.765137 6.98161 0.765137Z"
															strokeWidth="1"
														/>
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M1.41943 7.05933c0.18335 2.4085 2.18755 4.30967 5.54401 4.30967 3.14936 0 5.23766 -1.90649 5.61746 -4.30967"
															strokeWidth="1"
														/>
													</svg>
												) : (
													<svg className={`w-8 h-8 ${isDark ? "text-gray-500" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 24 24">
														<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
													</svg>
												)}
											</div>

											{/* Rating */}
											{showRating && review.rating > 0 && (
												<div className="mb-6">
													<div className="flex items-center gap-0.5">
														{[1, 2, 3, 4, 5].map((star) => (
															<svg
																key={star}
																className={`w-4 h-4 ${star <= review.rating ? (ratingStarColor ? "" : "text-amber-400") : isDark ? "text-gray-600" : "text-gray-300"}`}
																style={star <= review.rating && ratingStarColor ? { color: ratingStarColor } : undefined}
																fill={star <= review.rating ? "currentColor" : "none"}
																stroke="currentColor"
																viewBox="0 0 24 24"
															>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth={2}
																	d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
																/>
															</svg>
														))}
													</div>
												</div>
											)}

											{/* Video thumbnail */}
											{review.content_type === "video" && review.video_thumbnail_url && (
												<button
													type="button"
													onClick={() => review.mux_playback_id && setActiveVideoPlaybackId(review.mux_playback_id)}
													className="mb-4 -mx-1 -mt-1 overflow-hidden rounded-xl relative block w-[calc(100%+0.5rem)] cursor-pointer"
												>
													<img src={review.video_thumbnail_url} alt="" className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105" />
													<div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
														<div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg" style={{ backgroundColor: "#1f1f1f" }}>
															<svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
																<path d="M8 5v14l11-7z" />
															</svg>
														</div>
													</div>
												</button>
											)}

											{/* Audio player */}
											{review.content_type === "audio" && review.audio_url && <AudioPlayer src={review.audio_url} isDark={isDark} initialDuration={review.audio_duration} />}

											{/* Content */}
											<p className={`text-sm leading-relaxed mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{review.content_text}</p>

											{/* Author info */}
											<div className={`flex items-center gap-3 pt-3 border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}>
												{showAvatar && (
													review.customer_avatar_url ? (
														<img src={review.customer_avatar_url} alt={review.customer_name} className="w-10 h-10 rounded-full object-cover" />
													) : (
														<div
															className={`w-10 h-10 rounded-full bg-linear-to-br ${getGradient(
																review.customer_name,
															)} flex items-center justify-center text-white text-xs font-medium shadow-lg`}
														>
															{getInitials(review.customer_name)}
														</div>
													)
												)}
												<div className="flex-1 min-w-0">
													<p className={`font-medium text-sm truncate ${isDark ? "text-white" : "text-gray-900"}`}>{review.customer_name}</p>
													{customerInfo && <p className={`text-xs truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}>{customerInfo}</p>}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</Masonry>
					</ResponsiveMasonry>
				)}

				{/* Load More Button */}
				{!isLoading && nextCursor && (
					<div className={`flex justify-center py-6 sm:py-8 ${isDark ? "bg-gray-900" : ""}`}>
						<button
							type="button"
							onClick={onLoadMore}
							disabled={isLoadingMore}
							className={`inline-flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
								isDark
									? "text-gray-300 bg-gray-800 border border-gray-700 hover:bg-gray-750 hover:text-white"
									: "text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-900"
							}`}
						>
							{isLoadingMore ? (
								<>
									<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									Loading...
								</>
							) : (
								"Load More"
							)}
						</button>
					</div>
				)}
			</div>

			{/* Powered by branding */}
			{!isLoading && showBranding && (
				<div className={`py-4 flex justify-center text-center ${isDark ? "text-gray-500" : "text-gray-400"}`}>
					<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
						<img src="/images/badge-sayaboutus.svg" alt="build social proof with Say About Us" className="h-5 w-auto" />
					</a>
				</div>
			)}

			{/* Video Modal */}
			{activeVideoPlaybackId && (
				<VideoModal open={!!activeVideoPlaybackId} onOpenChange={(open) => !open && setActiveVideoPlaybackId(null)} playbackId={activeVideoPlaybackId} />
			)}
		</div>
	);
}
